import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import RouteErrorBoundary from './RouteErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import API from '../lib/api';
import { LayoutDashboard, FolderKanban, Clock, Receipt, Settings, LogOut, Bell, Menu, Columns3, BookOpen, Users, PanelLeftClose, PanelLeft, Sun, Moon, Lightbulb, EyeOff, Eye, ClipboardCheck, Cake, Coins, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useTheme } from '../contexts/ThemeContext';

const NAV = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/projects', icon: FolderKanban, label: 'Projects', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/pitch-internal', icon: Lightbulb, label: 'Pitch & Internal', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/kanban', icon: Columns3, label: 'Production Board', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/overtime-claims', icon: Clock, label: 'Overtime Claims', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/holidays', icon: Cake, label: 'Holidays', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/reimbursement-claims', icon: Receipt, label: 'Reimbursements', roles: ['super_admin','admin','project_manager','employee','finance'] },
  { path: '/zoho-books', icon: BookOpen, label: 'Zoho Books', roles: ['super_admin','admin','finance'], hideInPrivacy: true },
  { path: '/finance/reconciliation', icon: Coins, label: 'Reconciliation', roles: ['super_admin','admin','finance'], hideInPrivacy: true },
  { path: '/estimates', icon: FileText, label: 'Estimates', roles: ['super_admin'], departmentAllows: ['sales'] },
  { path: '/debrief-analytics', icon: ClipboardCheck, label: 'Debrief Analytics', roles: ['super_admin'] },
  { path: '/directory', icon: Users, label: 'Organization', roles: ['super_admin','admin'] },
  { path: '/settings', icon: Settings, label: 'Settings', roles: ['super_admin','admin'] },
];

function SidebarContent({ user, onItemClick, onLogout, collapsed, onToggle, privacyMode }) {
  const userDept = (user?.department || '').toLowerCase();
  const filtered = NAV.filter(n => {
    const roleOk = n.roles.includes(user?.role);
    const deptOk = (n.departmentAllows || []).includes(userDept);
    if (!roleOk && !deptOk) return false;
    return !(privacyMode && n.hideInPrivacy);
  });
  return (
    <div className="flex flex-col h-full bg-[#0f0f10] border-r border-zinc-800">
      <div className={`border-b border-zinc-800 ${collapsed ? 'p-3' : 'p-5'}`}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <img src="/filmbaker-logo.png" alt="Filmbaker" className={`drop-shadow-[0_0_8px_rgba(255,255,255,0.12)] ${collapsed ? 'h-6' : 'h-8'}`} />
        </div>
        {!collapsed && <p className="text-[11px] text-zinc-400 tracking-[0.35em] uppercase mt-1.5 w-full text-center font-medium" style={{letterSpacing: '0.35em'}}>ENGINE</p>}
      </div>
      <ScrollArea className={`flex-1 py-4 ${collapsed ? 'px-2' : 'px-3'}`}>
        <nav className="space-y-1">
          <TooltipProvider delayDuration={0}>
            {filtered.map(item => (
              collapsed ? (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink to={item.path} end={item.path === '/'} onClick={onItemClick}
                      className={({ isActive }) => `flex items-center justify-center p-2.5 rounded-md transition-all duration-200 ${isActive ? 'bg-white text-[#0f0f10]' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/60'}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <item.icon className="w-4 h-4" />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-zinc-800 text-white border-zinc-700 text-xs">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <NavLink key={item.path} to={item.path} end={item.path === '/'} onClick={onItemClick}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive ? 'bg-white text-[#0f0f10] font-semibold' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/60'}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              )
            ))}
          </TooltipProvider>
        </nav>
      </ScrollArea>
      <div className={`border-t border-zinc-800 ${collapsed ? 'p-2' : 'p-4'}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 mb-3">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-md object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-md bg-zinc-700 flex items-center justify-center text-xs font-bold text-white">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              {user?.designation && <p className="text-[10px] text-zinc-500 truncate">{user.designation}</p>}
            </div>
          </div>
        )}
        <div className={`flex ${collapsed ? 'flex-col' : ''} gap-1`}>
          {collapsed ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={onLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-zinc-800 text-white border-zinc-700 text-xs">Sign Out</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="w-full text-zinc-500 hover:text-white hover:bg-zinc-800" onClick={onToggle} data-testid="expand-sidebar-btn">
                    <PanelLeft className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-zinc-800 text-white border-zinc-700 text-xs">Expand</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="flex-1 justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={onLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </Button>
              <Button variant="ghost" size="icon" className="text-zinc-500 hover:text-white hover:bg-zinc-800 flex-shrink-0" onClick={onToggle} data-testid="collapse-sidebar-btn">
                <PanelLeftClose className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, privacyMode, togglePrivacy } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });

  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    const fetch = () => API.get('/notifications').then(r => setNotifications(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, []);

  const unread = (Array.isArray(notifications) ? notifications : []).filter(n => !n.read).length;
  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="flex h-screen bg-[#0f0f10]">
      <aside className={`hidden lg:block flex-shrink-0 relative z-20 transition-all duration-200 ${collapsed ? 'w-14' : 'w-60'}`}>
        <SidebarContent user={user} onLogout={handleLogout} collapsed={collapsed} onToggle={toggleCollapse} privacyMode={privacyMode} />
      </aside>
      <div className="flex-1 flex flex-col overflow-hidden relative z-10">
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 lg:px-6 bg-[#0f0f10]/80 backdrop-blur-sm relative z-10">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-zinc-400" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-60 bg-[#0f0f10] border-zinc-800">
              <SidebarContent user={user} onItemClick={() => setMobileOpen(false)} onLogout={handleLogout} collapsed={false} onToggle={() => {}} privacyMode={privacyMode} />
            </SheetContent>
          </Sheet>
          <div className="lg:hidden" />
          <div className="flex items-center gap-1">
            {['super_admin', 'admin'].includes(user?.role) && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={togglePrivacy}
                      className={`relative ${privacyMode ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="privacy-toggle-btn">
                      {privacyMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {privacyMode && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-zinc-800 text-white border-zinc-700 text-xs">
                    {privacyMode ? 'Privacy Mode ON (Ctrl+Shift+P)' : 'Privacy Mode OFF (Ctrl+Shift+P)'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-zinc-400 hover:text-white" data-testid="theme-toggle-btn">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative text-zinc-400 hover:text-white" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-600 rounded-full text-[10px] flex items-center justify-center text-white font-bold">{unread}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-[#18181b] border-zinc-700">
                <div className="p-3 border-b border-zinc-700 flex justify-between items-center">
                  <span className="text-sm font-semibold text-white font-heading">Notifications</span>
                  {unread > 0 && <Button variant="ghost" size="sm" className="text-xs text-zinc-400 h-7"
                    onClick={() => API.put('/notifications/read-all').then(() => setNotifications(n => n.map(x => ({...x, read: true}))))}>
                    Mark all read
                  </Button>}
                </div>
                <ScrollArea className="max-h-72">
                  {(Array.isArray(notifications) ? notifications : []).length === 0 ? (
                    <p className="p-6 text-sm text-zinc-500 text-center">No notifications yet</p>
                  ) : (Array.isArray(notifications) ? notifications : []).slice(0, 10).map(n => (
                    <DropdownMenuItem key={n.id} className={`flex flex-col items-start p-3 cursor-pointer ${!n.read ? 'bg-zinc-800/40' : ''}`}
                      onClick={() => { API.put(`/notifications/${n.id}/read`); if(n.link) navigate(n.link); }}>
                      <span className="text-sm text-white font-medium">{n.title}</span>
                      <span className="text-xs text-zinc-400 mt-0.5">{n.message}</span>
                    </DropdownMenuItem>
                  ))}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="p-4 lg:p-6 max-w-[1440px] mx-auto">
            <RouteErrorBoundary routeKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
